import { useState, useEffect } from "react";
import { Crosshair, LogOut } from "lucide-react";
import { useCanvasStore } from "../../stores/canvas.js";
import {
  defaultSecurity,
  securityLevelOf,
  SECURITY_LEVEL_OPTIONS,
  type SecurityLevel,
  type AgentSecurityProfile,
} from "../../data/security-defaults.js";

const inputClass =
  "w-full bg-transparent border-b border-border-subtle/40 px-0 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-purple transition-colors duration-200";

/**
 * Live editor for a builder agent node. Edits write straight through to the
 * canvas store, so the canvas and the eventual saved topology stay in sync.
 */
export function AgentConfigPanel() {
  const selectedNodeId = useCanvasStore((s) => s.selectedNodeId);
  const node = useCanvasStore((s) => s.nodes.find((n) => n.id === s.selectedNodeId));
  const updateNode = useCanvasStore((s) => s.updateNode);
  const setEntryPoint = useCanvasStore((s) => s.setEntryPoint);
  const toggleExitPoint = useCanvasStore((s) => s.toggleExitPoint);

  const [name, setName] = useState("");
  const [backend, setBackend] = useState("");
  const [model, setModel] = useState("");
  const [persona, setPersona] = useState("");
  const [instructions, setInstructions] = useState("");
  const [capabilities, setCapabilities] = useState("");

  useEffect(() => {
    if (!node) return;
    const d = node.data as Record<string, any>;
    setName(d.name ?? d.label ?? "");
    setBackend(d.backend ?? "claude-agent-sdk");
    setModel(d.model ?? "");
    setPersona(d.identity?.persona ?? "");
    setInstructions(
      Array.isArray(d.identity?.instructions)
        ? d.identity.instructions.join("\n")
        : d.identity?.instructions ?? "",
    );
    setCapabilities((d.capabilities ?? []).join(", "));
    // Re-sync only when the selected node changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  if (!node || !selectedNodeId) return null;
  const id = selectedNodeId;
  const d = node.data as Record<string, any>;
  const level = securityLevelOf(d.security as AgentSecurityProfile | undefined);
  const currentIdentity = () =>
    (useCanvasStore.getState().nodes.find((n) => n.id === id)?.data as any)?.identity ?? {};

  return (
    <div>
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <span className="text-xs font-medium text-text-muted">Agent</span>
        <button
          onClick={() => useCanvasStore.getState().selectNode(null)}
          className="text-sm text-text-muted transition-[color,transform] duration-200 hover:text-text-primary active:scale-[0.96]"
        >
          Close
        </button>
      </div>

      <div className="space-y-5 px-5 py-3">
        {/* Entry / exit role */}
        <div className="flex gap-2">
          <RoleToggle
            active={!!d.isEntryPoint}
            onClick={() => setEntryPoint(id)}
            icon={<Crosshair className="h-3.5 w-3.5" />}
            label="Entry"
            tone="green"
          />
          <RoleToggle
            active={!!d.isExitPoint}
            onClick={() => toggleExitPoint(id)}
            icon={<LogOut className="h-3.5 w-3.5" />}
            label="Exit"
            tone="cyan"
          />
        </div>

        <Field label="Name">
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              updateNode(id, { name: e.target.value, label: e.target.value });
            }}
            className={inputClass}
          />
        </Field>

        <Field label="Backend">
          <select
            value={backend}
            onChange={(e) => {
              setBackend(e.target.value);
              updateNode(id, { backend: e.target.value });
            }}
            className={inputClass}
          >
            <option value="claude-agent-sdk">Claude Agent SDK</option>
            <option value="openai-agents">OpenAI Agents</option>
            <option value="cloudflare-agents">Cloudflare Agents</option>
            <option value="raw-api">Raw API</option>
            <option value="custom">Custom</option>
          </select>
        </Field>

        <Field label="Model">
          <input
            value={model}
            onChange={(e) => {
              setModel(e.target.value);
              updateNode(id, { model: e.target.value });
            }}
            placeholder="e.g. sonnet, gpt-4o"
            className={inputClass}
          />
        </Field>

        <Field label="Persona">
          <textarea
            value={persona}
            onChange={(e) => {
              setPersona(e.target.value);
              updateNode(id, { identity: { ...currentIdentity(), persona: e.target.value } });
            }}
            rows={2}
            placeholder="You are a..."
            className="w-full resize-none rounded-lg border border-border-subtle/20 bg-surface/30 px-3 py-2 text-sm leading-relaxed text-text-primary placeholder-text-muted transition-colors duration-200 focus:border-brand-purple/40 focus:outline-none"
          />
        </Field>

        <Field label="Instructions">
          <textarea
            value={instructions}
            onChange={(e) => {
              setInstructions(e.target.value);
              updateNode(id, { identity: { ...currentIdentity(), instructions: e.target.value } });
            }}
            rows={4}
            placeholder="What should this agent do?"
            className="w-full resize-none rounded-lg border border-border-subtle/20 bg-surface/30 px-3 py-2 text-sm leading-relaxed text-text-primary placeholder-text-muted transition-colors duration-200 focus:border-brand-purple/40 focus:outline-none"
          />
        </Field>

        <Field label="Capabilities">
          <input
            value={capabilities}
            onChange={(e) => {
              setCapabilities(e.target.value);
              updateNode(id, {
                capabilities: e.target.value
                  .split(",")
                  .map((c) => c.trim())
                  .filter(Boolean),
              });
            }}
            placeholder="comma, separated, tags"
            className={inputClass}
          />
        </Field>

        <Field label="Security">
          <select
            value={level}
            onChange={(e) => updateNode(id, { security: defaultSecurity(e.target.value as SecurityLevel) })}
            className={inputClass}
          >
            {SECURITY_LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

function RoleToggle({
  active,
  onClick,
  icon,
  label,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  tone: "green" | "cyan";
}) {
  const onCls = tone === "green" ? "bg-brand-green/15 text-brand-green" : "bg-brand-cyan/15 text-brand-cyan";
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-[color,background-color,border-color,transform] duration-200 active:scale-[0.97] ${
        active
          ? `${onCls} border-transparent`
          : "border-border-subtle/30 text-text-muted hover:text-text-secondary"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs text-text-muted">{label}</label>
      {children}
    </div>
  );
}
